/**
 * gtm.ts — the A1 CSP-clean Google Tag Manager loader, served first-party.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: tracking A1.
 *
 * WHAT IS FIRST-PARTY HERE: the GTM WEB CONTAINER. It is fetched from our OWN
 * tagging server (`sst.thegpservice.co.uk`, self-hosted sGTM on Cloud Run)
 * instead of googletagmanager.com. The loader injects an EXTERNAL `<script>`
 * from that server; the gtm.start bootstrap is pushed from our own bundle
 * (origin `'self'`), so there is NO inline `<script>` and the CSP never needs
 * `'unsafe-inline'`. It bootstraps the SAME `window.dataLayer` array that
 * `dataLayer.ts` already pushes the PHI-safe funnel events to.
 *
 * WHAT IS *NOT* FIRST-PARTY — READ BEFORE TOUCHING THE CSP. An earlier version of
 * this docblock claimed the tagging server meant "the CSP only needs that ONE
 * origin, and every tag request stays first-party". That is false, and believing
 * it silently disabled all advertising measurement. Serving the CONTAINER
 * first-party does not serve the TAG LIBRARIES first-party: the container we
 * serve still instructs the browser to fetch `gtag.js` from
 * www.googletagmanager.com, and the Google Ads tag then beacons to Google's
 * conversion endpoints direct from the browser. Those origins must be on the CSP
 * or the Ads tag never initialises at all — see nginx.conf and the measured
 * evidence in ./csp.spec.ts. Nothing in THIS file can change that; the host is
 * baked into the container that GTM builds.
 *
 * Making the tag libraries first-party too is a GTM-side change (the web
 * container's Google tags need `server_container_url`, and the tagging server's
 * Web Container client must be allowed to serve dependent Google scripts). Until
 * that is configured AND re-measured, the CSP entries stay.
 *
 * PATH-GATED (Salman's spec): only the acquisition journey loads GTM. The
 * signed-in app is NEVER tracked — `/patients` (patient portal) is EXCLUDED and
 * `/clinicians` + `/admin` (staff) are SUPPRESSED — so a clinician or a
 * returning patient session never even loads the container. `/auth` is excluded
 * too (sign-in is not an acquisition surface).
 *
 * Idempotent: GTM loads at most once per document. Fail-closed: when the
 * container id is unset, `loadGtm()` no-ops (a misconfigured build ships without
 * analytics rather than pointing at the wrong container).
 */
import { gtmContainerId, gtmServerUrl } from '../config';
import { PREFERENCES_PATH } from '../marketing/route';
import { CLINICAL_SAFETY_LEGACY_PATH, CLINICAL_SAFETY_PATH } from '../safety/route';
import { UNLOCK_PATH } from '../unlock/route';
import { initConsent } from './consent';

/**
 * Reserved top-level segments that are NOT clinic journeys — the signed-in app
 * (`/patients`, `/clinicians`, `/admin`, `/pharmacy`) and the two auth front
 * doors. A clinic slug can never collide with these, so they are excluded even
 * though they are single-segment.
 *
 * `pharmacy` and `auth-pharmacy` (2026-07-29) matter more than the rest: a
 * SINGLE-segment path is otherwise treated as a clinic journey and TRACKED, so
 * without these two entries the dispensing console and its sign-in would have
 * loaded GTM and shown a patient cookie banner to a pharmacist — ad-tech on a
 * surface that renders patient names, addresses and a clinical extract. The
 * single-segment rule is the one place this module fails OPEN; any future
 * top-level app route must be added here in the same commit that adds the route.
 */
const RESERVED_SEGMENTS = new Set([
  'patients',
  'clinicians',
  'admin',
  'auth',
  'pharmacy',
  'auth-pharmacy',
  // The GPS Core → Clinics sign-in handoff (Clinics-in-Core, 2026-09-07). A
  // single-segment path, so without this entry the allow-list below would read
  // it as a clinic journey and load GTM on it — on the page that receives a
  // signed-in Core patient's sign-in token in its fragment. That token is a
  // credential, and the person arriving is by definition an existing patient,
  // not acquisition traffic. Same rule as `auth` and `auth-pharmacy`.
  'auth-core',
  // The end-of-term re-subscribe confirmation (2026-08-15). A single-segment
  // path, so without this entry the allow-list below would read it as a clinic
  // journey and load GTM on it — on a page that exists BECAUSE a named patient
  // is on a named treatment, reached from a link in their own inbox. That is
  // exactly the category leak Part F exists to prevent, and the fact that the
  // page is unauthenticated makes it MORE important, not less.
  'resubscribe',
  // The public clinical-safety page and the CTO's original misspelling of its
  // URL (2026-08-20). Single-segment paths again, so without these entries the
  // allow-list would read them as clinic journeys and load GTM.
  //
  // This is the sharpest case yet for keeping ad-tech off a page. The clinic
  // journey is category-level copy by R28 design; this page names
  // prescription-only medicines outright, and it is reached by someone who has
  // either bought one or is deciding whether to. A GTM container on it would
  // put "which named POM was this person reading about" into an analytics/ads
  // pipeline — health-category inference of exactly the kind the Part F
  // guardrails exist to prevent, and no consent banner makes that acceptable.
  // Derived from the route constants rather than re-typed: one of the two IS a
  // misspelling, and a segment list that disagrees with the route table by a
  // letter fails silently — the page still loads, it just loads GTM with it.
  CLINICAL_SAFETY_PATH.slice(1),
  CLINICAL_SAFETY_LEGACY_PATH.slice(1),
  // The marketing-consent preferences page (2026-09-11). A single-segment path,
  // so without this entry it would be read as a clinic journey and load GTM — on
  // the page a person reaches from their own inbox to tell us how (or whether)
  // we may contact them, holding a signed link token. Tracking the page where
  // someone withdraws marketing consent would be absurd as well as a leak.
  // Derived from the route constant, like the two entries above.
  PREFERENCES_PATH.slice(1),
  // The engagement unlock page (2026-09-23). A single-segment path, so without
  // this entry the allow-list below would read it as a clinic journey and load
  // GTM — on the page a named patient reaches from their own inbox or a
  // WhatsApp thread, holding a capability token in its fragment. The person
  // arriving is by definition a known lead or patient on a named clinic, not
  // acquisition traffic, and the token is a credential. Derived from the route
  // constant, like the entries above.
  UNLOCK_PATH.slice(1),
  // The patient account pages (returning patients S7.1, 2026-09-25), reserved
  // BEFORE the first one ships. `/account/confirm#t=…` (S1.7) is where a
  // patient lands from their own inbox to confirm their email or finish their
  // account, holding a single-use token in the fragment. That path is two
  // segments deep, so the allow-list below already leaves it untracked, but a
  // bare `/account` or an `/account/callback` would be read as a clinic
  // journey and load GTM. Reserving the whole segment keeps every current and
  // future account page untracked. A literal, because no route constant
  // exists yet: S1.7 builds its path under this segment.
  'account',
  // ⚠️ THE THANK-YOU ROUTE IS DELIBERATELY *NOT* IN THIS LIST (2026-08-20).
  //
  // It is a new top-level route, so the rule stated above applies to it and has
  // been answered rather than skipped: it stays TRACKED. It is the last screen
  // of the acquisition journey — the conversion page the container exists to
  // measure — and unlike every entry above it, it is reached by finishing a
  // purchase rather than by being a named patient on a named treatment. It also
  // carries nothing: no order id, no reference, no assessment id, no medication
  // and no price, in the path or the query (see checkout/thankYouRoute.ts), so
  // there is no category leak for a reservation to prevent.
  //
  // Adding it here would switch marketing's conversion measurement off silently
  // — the page would still render perfectly. `tracking/gtm.spec.ts` asserts the
  // path is tracked, so that mistake fails CI instead of a monthly report.
]);

/**
 * True when this document already injected GTM and the next route is not an
 * acquisition path. The container stays alive for the life of the document, so
 * after a client-side hop from `/weightloss` into `/patients` or `/auth` the
 * tag would go on running over the signed-in DOM, the /auth password form and
 * every later path (Part F). The caller reloads once; the new document never
 * loads GTM (`isTrackedPath` is false).
 *
 * This bounds exposure AFTER the hop. It is not the same as a `ZoneExitLink`:
 * the router pushes the untracked URL before the caller runs, so the GTM
 * document still sees that one URL. And it is not a bearer control — the
 * checkout mints the session on the tracked journey while GTM is loaded.
 * Direct visits to staff routes are not reloaded.
 */
export function shouldDropGtmDocument(pathname: string, gtmLoaded: boolean): boolean {
  return gtmLoaded && !isTrackedPath(pathname);
}

/**
 * Whether GTM may load for this pathname — an ALLOW-LIST (fail-CLOSED). Only the
 * closed acquisition-journey shapes are tracked: the root `/`, a clinic journey
 * `/:clinicSlug` (e.g. `/weightloss`), and its `/:clinicSlug/callback`. Anything
 * else — the signed-in app, auth, and ANY future/deeper route — is untracked by
 * default, so forgetting to exclude a new route is a missing-analytics bug, not
 * a data-protection incident. Pure; safe on every navigation.
 */
export function isTrackedPath(pathname: string): boolean {
  const segments = pathname.replace(/^\/+|\/+$/g, '').split('/').filter(Boolean);
  if (segments.length === 0) return true; // root `/`
  if (RESERVED_SEGMENTS.has(segments[0])) return false; // the signed-in app / auth
  if (segments.length === 1) return true; // `/:clinicSlug` journey
  if (segments.length === 2 && segments[1] === 'callback') return true; // `/:clinicSlug/callback`
  return false; // anything deeper is NOT an acquisition surface
}

interface GtmWindow {
  dataLayer?: unknown[];
  __gpsGtmLoaded?: boolean;
}

/**
 * Load the first-party GTM container ONCE. No-op when: not in a browser, the
 * container id is unset (fail-closed), or GTM has already been injected. Safe to
 * call on every tracked navigation.
 */
export function loadGtm(): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const id = gtmContainerId();
  if (!id) return; // fail-closed: no container id → no tracking
  const w = window as unknown as GtmWindow;
  if (w.__gpsGtmLoaded) return;
  w.__gpsGtmLoaded = true;

  // Consent Mode v2 FIRST: push the default-denied state (+ re-apply any stored
  // grant) BEFORE the container reads anything, so GTM/GA4 respect consent from
  // the very first hit and set no ad/analytics cookies until the visitor opts in.
  initConsent();

  // Bootstrap gtm.start onto the SAME array the consent + funnel helpers use.
  // initConsent() has already created window.dataLayer (its first push), so we
  // append straight onto it. This runs from our bundle ('self') — never an
  // inline <script> — so the CSP stays tight.
  (w.dataLayer as unknown[]).push({ 'gtm.start': Date.now(), event: 'gtm.js' });

  // Inject the EXTERNAL container loader from our own tagging server. `/gtm.js`
  // is the canonical path the sGTM Web Container client answers on — verified
  // 200 against sst.thegpservice.co.uk. Do NOT "fix" this to `/gtm-js`: that was
  // tried, returns 400, and would take tracking down completely. (A tag-serving
  // path of `/gtm-js` was configured on the GTM client at one point; Google's own
  // docs disallow such a path — it must not contain `/gtm` — and nothing is
  // served there under any request shape.)
  const script = document.createElement('script');
  script.async = true;
  script.src = `${gtmServerUrl()}/gtm.js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
}
